import { ROUTES } from '@/app/routes'
import { DEFAULT_LOCALE, LOCALE_CODES } from '@/i18n/locales'

/**
 * An internal link, pointed at the locale being read — where that page exists.
 *
 * Every href in the dictionary is written once, in English, as `/en/…`: the
 * `LINKS` table in `content/pages/links.ts` is the only place a path is spelled
 * out. Without this, a Turkish reader clicking "Çözümler" lands on the English
 * page, because the href came from the base dictionary.
 *
 * **Why rewrite at render instead of per locale.** A locale file *could* restate
 * every href — `Translation` makes arrays atomic, so a locale that translates
 * `nav.items` supplies the whole array anyway. But then six locales hold six
 * copies of the same path and a page that moves has to move in six files, and the
 * failure is silent and asymmetric: the locales that restated the nav stay correct
 * while the ones that did not send their readers to English. Doing it here also
 * means an *untranslated* section still links within the reader's language.
 *
 * **Only the written routes are rewritten, and that is the whole subtlety.**
 * Naively swapping the prefix on every path is worse than not rewriting at all: it
 * points the Turkish nav at `/tr/customers`, `/tr/blog` and `/tr/privacy-policy`,
 * none of which exist. Every written route is prerendered in every locale (English
 * shows through where a locale has not written it), so those are always safe. The
 * imported digitopia.co pages exist only in the languages they were imported in,
 * and the Turkish ones carry *Turkish slugs* — `/tr/butce-donemi`, not
 * `/tr/budget-season` — so there is no prefix swap that could find them.
 *
 * Which leaves a real gap, recorded rather than hidden: a Turkish reader clicking
 * "Müşteriler" gets the English customers page. That is honest today (there is no
 * Turkish one) and it becomes wrong the moment somebody writes one. The fix then
 * is a slug map per locale, not a change here.
 */
const WRITTEN_PATHS: ReadonlySet<string> = new Set(ROUTES.map((r) => r.path))

/**
 * Imported pages that exist in another locale **at the same slug**.
 *
 * The gap described above, closed one page at a time rather than by a slug map
 * nobody needs yet: Contact Us was written in Turkish on 2026-08-31 and its
 * address is `contact-us` in both languages, so the Turkish nav's "Toplantı
 * Planlayın" — which reads `/en/contact-us` out of `LINKS`, like every other
 * href in the dictionary — can now be pointed at the Turkish page.
 *
 * Only put a slug here once that page actually exists in that locale. An entry
 * with no page behind it sends readers to a 404, which is worse than the
 * English page this is fixing.
 */
const LEGACY_LOCALE_PATHS: Record<string, ReadonlySet<string>> = {
  tr: new Set(['contact-us']),
}

export function localizeHref(href: string, locale: string): string {
  // A PAUSED locale navigates in English, and it has to.
  //
  // `/es/que-es-la-transformacion-digital` is a real Spanish page that survives
  // the pause of `/es` (see `LocaleDef.live` and the Worker's paused-locale
  // block). Without this line its nav, footer and every CTA rewrite to
  // `/es/solutions`, `/es/tools/...` and eleven more URLs that are not built —
  // thirteen dead links on one page, which is what `verify-links` reported the
  // moment the locale went dark. The Worker would 302 each of them to English
  // at click time, so a visitor was never stranded; the links were simply
  // wrong, and a page whose whole chrome needs a redirect hop is not a page
  // that works.
  //
  // So a locale the site does not serve gets the English chrome around its own
  // words. That is the honest shape of the thing: the page is Spanish, the site
  // it sits in currently speaks English and Turkish.
  if (!LOCALE_CODES.includes(locale)) return href

  // Anything that is not one of our own English paths is returned untouched:
  // absolute URLs (impact.digitopia.ai, LinkedIn), hash links (`#contact`), and
  // paths that already name a locale.
  if (locale === 'en' || !href.startsWith('/en')) return href

  const rest = href.slice('/en'.length)
  // `/en` itself, or `/en/…`. Not `/energy` — a prefix match on the string would
  // happily rewrite that into `/trergy`.
  if (rest !== '' && !rest.startsWith('/')) return href

  const path = rest.replace(/^\//, '').replace(/\/$/, '')
  if (!WRITTEN_PATHS.has(path) && !LEGACY_LOCALE_PATHS[locale]?.has(path)) return href

  return path ? `/${locale}/${path}` : `/${locale}`
}

/**
 * The locale a page's CHROME navigates in — its own, unless the site does not
 * serve that locale, in which case English.
 *
 * `localizeHref` above handles every href that comes out of the dictionary.
 * This exists for the two links built from the locale directly rather than from
 * a path — the logo in `Layout` and the way back in `NotFound`, both
 * `localePath(locale, '')` — which is how one dead link (`/es`) survived the
 * twelve that line fixed.
 */
export function navLocale(locale: string): string {
  return LOCALE_CODES.includes(locale) ? locale : DEFAULT_LOCALE
}
